// Phase templates for different task categories
// Each template defines a workflow with sequential phases and their requirements

export interface PhaseRequirement {
  id: string;
  description: string;
  completed: boolean;
  type: 'manual' | 'document' | 'signature';
}

export interface DocumentRequirement {
  document_type: string;
  template_id?: string;
  generated_document_id?: string;
  status: 'pending' | 'generated' | 'finalized' | 'signed';
  required: boolean;
  description: string;
}

export interface SignatureRequirement {
  document_id?: string;
  signature_request_id?: string;
  required_roles: string[];
  status: 'pending' | 'in_progress' | 'completed';
  required: boolean;
  description: string;
}

export interface PhaseTemplate {
  phase_number: number;
  phase_name: string;
  phase_description: string;
  order_number: number;
  estimated_duration_days: number;
  requirements: PhaseRequirement[];
  document_requirements: DocumentRequirement[];
  signature_requirements: SignatureRequirement[];
}

export interface CategoryPhaseTemplate {
  category_type: string;
  category_name: string;
  phases: PhaseTemplate[];
}

// Phase templates by category
export const PHASE_TEMPLATES: CategoryPhaseTemplate[] = [
  {
    category_type: 'asset',
    category_name: 'Asset-Related',
    phases: [
      {
        phase_number: 1,
        phase_name: 'Asset Documentation',
        phase_description: 'Gather and document all asset information',
        order_number: 1,
        estimated_duration_days: 7,
        requirements: [
          { id: '1', description: 'Complete asset appraisal', completed: false, type: 'manual' },
          { id: '2', description: 'Verify ownership documents', completed: false, type: 'manual' },
          { id: '3', description: 'Document current market value', completed: false, type: 'manual' },
        ],
        document_requirements: [
          {
            document_type: 'asset_appraisal',
            status: 'pending',
            required: true,
            description: 'Asset Appraisal Report',
          },
        ],
        signature_requirements: [],
      },
      {
        phase_number: 2,
        phase_name: 'Transfer Agreement',
        phase_description: 'Create and execute transfer documents',
        order_number: 2,
        estimated_duration_days: 14,
        requirements: [
          { id: '1', description: 'Review transfer terms', completed: false, type: 'manual' },
          { id: '2', description: 'Confirm all parties informed', completed: false, type: 'manual' },
        ],
        document_requirements: [
          {
            document_type: 'transfer_agreement',
            status: 'pending',
            required: true,
            description: 'Asset Transfer Agreement',
          },
        ],
        signature_requirements: [
          {
            required_roles: ['treasury_secretary', 'managing_member'],
            status: 'pending',
            required: true,
            description: 'Transfer Agreement Signatures',
          },
        ],
      },
      {
        phase_number: 3,
        phase_name: 'Recording & Finalization',
        phase_description: 'Record transfer and complete process',
        order_number: 3,
        estimated_duration_days: 7,
        requirements: [
          { id: '1', description: 'Submit to appropriate registry', completed: false, type: 'manual' },
          { id: '2', description: 'Receive confirmation of recording', completed: false, type: 'manual' },
          { id: '3', description: 'Update asset records', completed: false, type: 'manual' },
        ],
        document_requirements: [],
        signature_requirements: [],
      },
    ],
  },
  {
    category_type: 'member',
    category_name: 'Member-Related',
    phases: [
      {
        phase_number: 1,
        phase_name: 'Member Onboarding',
        phase_description: 'Initial member setup and documentation',
        order_number: 1,
        estimated_duration_days: 5,
        requirements: [
          { id: '1', description: 'Collect member information', completed: false, type: 'manual' },
          { id: '2', description: 'Verify identity documents', completed: false, type: 'manual' },
          { id: '3', description: 'Assign member role', completed: false, type: 'manual' },
        ],
        document_requirements: [
          {
            document_type: 'member_agreement',
            status: 'pending',
            required: true,
            description: 'Member Agreement',
          },
        ],
        signature_requirements: [],
      },
      {
        phase_number: 2,
        phase_name: 'Agreement Execution',
        phase_description: 'Execute member agreements',
        order_number: 2,
        estimated_duration_days: 7,
        requirements: [
          { id: '1', description: 'Review agreement with member', completed: false, type: 'manual' },
        ],
        document_requirements: [],
        signature_requirements: [
          {
            required_roles: ['treasury_secretary', 'member'],
            status: 'pending',
            required: true,
            description: 'Member Agreement Signatures',
          },
        ],
      },
      {
        phase_number: 3,
        phase_name: 'Access & Activation',
        phase_description: 'Provide access and activate member',
        order_number: 3,
        estimated_duration_days: 2,
        requirements: [
          { id: '1', description: 'Grant system access', completed: false, type: 'manual' },
          { id: '2', description: 'Schedule orientation', completed: false, type: 'manual' },
          { id: '3', description: 'Add to communication channels', completed: false, type: 'manual' },
        ],
        document_requirements: [],
        signature_requirements: [],
      },
    ],
  },
  {
    category_type: 'governance',
    category_name: 'Governance-Related',
    phases: [
      {
        phase_number: 1,
        phase_name: 'Proposal Preparation',
        phase_description: 'Draft and review governance proposal',
        order_number: 1,
        estimated_duration_days: 10,
        requirements: [
          { id: '1', description: 'Draft proposal document', completed: false, type: 'manual' },
          { id: '2', description: 'Review with legal counsel', completed: false, type: 'manual' },
          { id: '3', description: 'Distribute to board members', completed: false, type: 'manual' },
        ],
        document_requirements: [
          {
            document_type: 'governance_proposal',
            status: 'pending',
            required: true,
            description: 'Governance Proposal',
          },
        ],
        signature_requirements: [],
      },
      {
        phase_number: 2,
        phase_name: 'Board Review',
        phase_description: 'Board discussion and approval',
        order_number: 2,
        estimated_duration_days: 14,
        requirements: [
          { id: '1', description: 'Schedule board meeting', completed: false, type: 'manual' },
          { id: '2', description: 'Present proposal to board', completed: false, type: 'manual' },
          { id: '3', description: 'Vote on proposal', completed: false, type: 'manual' },
        ],
        document_requirements: [
          {
            document_type: 'board_resolution',
            status: 'pending',
            required: true,
            description: 'Board Resolution',
          },
        ],
        signature_requirements: [
          {
            required_roles: ['treasury_secretary', 'board_member_active'],
            status: 'pending',
            required: true,
            description: 'Board Resolution Signatures',
          },
        ],
      },
      {
        phase_number: 3,
        phase_name: 'Implementation',
        phase_description: 'Execute approved changes',
        order_number: 3,
        estimated_duration_days: 21,
        requirements: [
          { id: '1', description: 'Update governing documents', completed: false, type: 'manual' },
          { id: '2', description: 'Communicate changes to members', completed: false, type: 'manual' },
          { id: '3', description: 'File required state documents', completed: false, type: 'manual' },
        ],
        document_requirements: [],
        signature_requirements: [],
      },
    ],
  },
  {
    category_type: 'contribution',
    category_name: 'Contribution-Related',
    phases: [
      {
        phase_number: 1,
        phase_name: 'Contribution Documentation',
        phase_description: 'Document contribution details',
        order_number: 1,
        estimated_duration_days: 3,
        requirements: [
          { id: '1', description: 'Verify contribution amount', completed: false, type: 'manual' },
          { id: '2', description: 'Document contribution source', completed: false, type: 'manual' },
          { id: '3', description: 'Record in accounting system', completed: false, type: 'manual' },
        ],
        document_requirements: [
          {
            document_type: 'contribution_receipt',
            status: 'pending',
            required: true,
            description: 'Contribution Receipt',
          },
        ],
        signature_requirements: [],
      },
      {
        phase_number: 2,
        phase_name: 'Acknowledgment',
        phase_description: 'Send acknowledgment to contributor',
        order_number: 2,
        estimated_duration_days: 2,
        requirements: [
          { id: '1', description: 'Generate acknowledgment letter', completed: false, type: 'manual' },
          { id: '2', description: 'Send to contributor', completed: false, type: 'manual' },
        ],
        document_requirements: [],
        signature_requirements: [
          {
            required_roles: ['treasury_secretary'],
            status: 'pending',
            required: true,
            description: 'Receipt Signature',
          },
        ],
      },
    ],
  },
  {
    category_type: 'compliance',
    category_name: 'Compliance-Related',
    phases: [
      {
        phase_number: 1,
        phase_name: 'Compliance Review',
        phase_description: 'Review compliance requirements',
        order_number: 1,
        estimated_duration_days: 7,
        requirements: [
          { id: '1', description: 'Identify applicable regulations', completed: false, type: 'manual' },
          { id: '2', description: 'Review current compliance status', completed: false, type: 'manual' },
          { id: '3', description: 'Document gaps and requirements', completed: false, type: 'manual' },
        ],
        document_requirements: [
          {
            document_type: 'compliance_report',
            status: 'pending',
            required: true,
            description: 'Compliance Report',
          },
        ],
        signature_requirements: [],
      },
      {
        phase_number: 2,
        phase_name: 'Filing & Submission',
        phase_description: 'Complete required filings',
        order_number: 2,
        estimated_duration_days: 10,
        requirements: [
          { id: '1', description: 'Complete required forms', completed: false, type: 'manual' },
          { id: '2', description: 'Submit to regulatory agencies', completed: false, type: 'manual' },
          { id: '3', description: 'Confirm receipt', completed: false, type: 'manual' },
        ],
        document_requirements: [],
        signature_requirements: [
          {
            required_roles: ['treasury_secretary', 'managing_member'],
            status: 'pending',
            required: true,
            description: 'Filing Signatures',
          },
        ],
      },
    ],
  },
];

// Helper function to get phase template by category
export function getPhaseTemplateByCategory(categoryType: string): CategoryPhaseTemplate | undefined {
  return PHASE_TEMPLATES.find(template => template.category_type === categoryType);
}

// Helper function to check if a category has phase templates
export function categoryHasPhases(categoryType: string): boolean {
  return PHASE_TEMPLATES.some(template => template.category_type === categoryType);
}

// Helper function to get estimated total duration
export function getTemplateTotalDuration(categoryType: string): number {
  const template = getPhaseTemplateByCategory(categoryType);
  if (!template) return 0;
  
  return template.phases.reduce((total, phase) => total + phase.estimated_duration_days, 0);
}
